import { useContext, useCallback } from 'react';
import MerchantContext from '@contexts/admin/MerchantContext';
import OperatorAuthContext from '@contexts/operator/OperatorAuthContext';
import type { Permission } from '@/permissions_manifest.generated';

const matchesPermissionList = (
  permissions: readonly string[] | undefined,
  resource: string,
  action: string,
): boolean => {
  if (!permissions || permissions.length === 0) return false;
  if (permissions.includes('*:*')) return true;
  if (permissions.includes(`${resource}:*`)) return true;
  if (permissions.includes(`*:${action}`)) return true;
  return permissions.includes(`${resource}:${action}`);
};

const parsePermission = (
  resourceOrCombined: string,
  action?: string,
): [string, string] => {
  if (action !== undefined) return [resourceOrCombined, action];
  const [resource, act] = String(resourceOrCombined).split(':');
  return [resource, act ?? ''];
};

export type CanFn = {
  (combined: Permission): boolean;
  (combined: string): boolean;
  (resource: string, action: string): boolean;
};

/**
 * useCan - 統一 admin / operator context 的權限檢查 hook
 *
 * 用法：
 *   const can = useCan();
 *   can('orders:write')          // → boolean
 *   can('orders', 'write')       // → boolean
 *
 * 兩個 auth context 互斥：同時存在代表 provider 配置錯誤，會 throw。
 * 都不存在時回傳永遠為 false 的函式（fail-closed）。
 */
const useCan = (): CanFn => {
  const merchantCtx = useContext(MerchantContext);
  const operatorCtx = useContext(OperatorAuthContext);

  if (merchantCtx && operatorCtx) {
    throw new Error(
      'useCan: MerchantContext and OperatorAuthContext are both active; admin and operator sessions must be mutually exclusive',
    );
  }

  // MerchantContext / OperatorAuthContext 仍是 .jsx，這裡用 narrow type 取出我們需要的欄位。
  const adminPermissions = (merchantCtx as { currentPermissions?: readonly string[] } | null)?.currentPermissions;
  const operatorPermissions = (operatorCtx as { permissions?: readonly string[] } | null)?.permissions;

  return useCallback(
    ((resourceOrCombined: string, action?: string) => {
      const [resource, act] = parsePermission(resourceOrCombined, action);
      if (!resource || !act) return false;

      if (merchantCtx) {
        return matchesPermissionList(adminPermissions, resource, act);
      }
      if (operatorCtx) {
        return matchesPermissionList(operatorPermissions, resource, act);
      }
      return false;
    }) as CanFn,
    [merchantCtx, operatorCtx, adminPermissions, operatorPermissions],
  );
};

export default useCan;
